import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { VideoAdapter } from '@app/adapters/_index';
import { HasAnalytics, HasPageTitleAndMetaData, VideoInterface } from '@app/interfaces/_index';
import { Video } from '@app/models/_index';
import { GoogleAnalyticsService } from '@app/services/_index';
import { WordpressVideosService } from '@app/services/wordpress/wordpress-videos.service';
import { logGoogleAnalytics } from '@app/utilities/console-log.utilities';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-video-detail',
  templateUrl: './video-detail.component.html',
  styleUrls: ['./video-detail.component.scss'],
  standalone: false,
})
export class VideoDetailComponent
  implements OnInit, OnDestroy, HasPageTitleAndMetaData, HasAnalytics
{
  subVideo: Video;
  featuredVideos: Video[] = [];
  isFeaturedVideo = false;
  subs: Subscription[] = [];
  isLoading: boolean;

  constructor(
    private titleService: Title,
    private metaService: Meta,
    private activeRoute: ActivatedRoute,
    private videoAdapter: VideoAdapter,
    public location: Location,
    private router: Router,
    private googleAnalyticsService: GoogleAnalyticsService,
    private wpVideoService: WordpressVideosService
  ) {
    this.router.routeReuseStrategy.shouldReuseRoute = () => false;
  }

  /**
   * OnInit setting page title and metadata
   * Set Video details, and load featured videos in the slider.
   */
  ngOnInit(): void {
    // Set title and metadata
    this.setTitleAndMetaData();
    // Subscribe and Load video details
    this.subs.push(
      this.activeRoute.params.subscribe((params: Params) => {
        this.getVideoDetails(params);
      })
    );
  }

  /**
   * Get the video details for the slug in the params from WordPress
   * @param params - route params that contain the video slug
   */
  getVideoDetails(params: any): void {
    this.isLoading = true;
    this.subs.push(
      this.wpVideoService.getWpRecommendedVideos().subscribe({
        next: (videos: VideoInterface[]) => {
          this.subVideo = this.videoAdapter.adapt(
            videos.filter((video: VideoInterface) => video.slug === params.slug)[0]
          );

          if (this.subVideo && this.subVideo.categories) {
            this.isLoading = false;

            for (const videoCategory of this.subVideo.categories) {
              if (videoCategory.slug === 'featured') {
                this.isFeaturedVideo = true;
                break;
              }
            }

            if (this.isFeaturedVideo) {
              this.getFeaturedVideos();
            }
          }
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          this.isLoading = false;
          console.log('Video details retrieval completed');
        },
      })
    );
  }

  /**
   * Get Featured Videos for Video Slider from WordPress
   */
  getFeaturedVideos(): void {
    this.isLoading = true;
    this.subs.push(
      this.wpVideoService.getWpFeaturedVideos().subscribe({
        next: (featuredVideos: VideoInterface[]) => {
          this.isLoading = false;
          featuredVideos.forEach((featuredVideo: VideoInterface) => {
            if (featuredVideo.id !== this.subVideo.id) {
              this.featuredVideos.push(this.videoAdapter.adapt(featuredVideo));
            }
          });
        },
        error: (error) => {
          this.isLoading = false;
          console.log(error);
        },
        complete: () => {
          this.isLoading = false;
          console.log('Video details retrieval completed');
        },
      })
    );
  }

  /**
   * Shows the previous video on click to Prev link and redirects the route accordingly
   * TODO: Refactor this to use the carousel for previous
   */
  gotoPreviousVideo(): void {
    // Splice removes first video and adds the new sub video to the end of the array
    this.featuredVideos.splice(0, 0, this.subVideo);
    this.subVideo = this.featuredVideos[this.featuredVideos.length - 1];
    this.featuredVideos.pop();
    this.location.go('/video/' + this.subVideo.slug);
  }

  /**
   * Shows the Next video on click to Prev link and redirects the route accordingly
   * TODO: Refactor this to use the carousel for next
   */
  gotoNextVideo(): void {
    this.featuredVideos.push(this.subVideo);
    this.subVideo = this.featuredVideos[0];
    this.featuredVideos.splice(0, 1);
    this.location.go('/video/' + this.subVideo.slug);
  }

  /**
   * Send the query search label to Google Analytics
   * @param category - the source of the google analytics category string (ex. the page or component triggering the action)
   * @param action - the action to be (ex. click)
   * @param dataLabel - the label associated with the action
   */
  sendGoogleAnalytics(category: string, action: string, dataLabel: string): void {
    logGoogleAnalytics(category, action, dataLabel);
    this.googleAnalyticsService.sendEvent(category, action, dataLabel);
  }

  /**
   * Set page title and metadata tags
   */
  setTitleAndMetaData(): void {
    // Set title for default main page
    this.titleService.setTitle('Video Detail | Genesys Education');
    // Set meta tag for description for beyond
    this.metaService.updateTag({
      name: 'description',
      content:
        'Learn new technologies and the necessary business skills to grow in the world of customer experience, including artificial intelligence, workforce management and more.',
    });
  }

  /**
   * On destroy unsubscribe the subscription to videos, to prevent
   * memory leaks
   */
  ngOnDestroy(): void {
    if (this.subs) {
      this.subs.forEach((sub) => {
        sub.unsubscribe();
      });
    }
  }
}
